import React from 'react';
import Dropzone from 'react-dropzone';
import {
	ApButtonMain,
	ApIcon,
	ApButtonSecondary,
} from '@alixpartners/ui-components';
import { uploadExcelFile } from 'utils/apiRequests';
import { ApSignalrConsumer } from '@alixpartners/ui-utils';
import { brandColors, grey200, chartOrange } from 'styles/colors';
import { XlsIcon } from 'components/icons';
import { bytesToSize } from 'Settings/utils';
import ErrorList from '../../components/ErrorList';
import {
	DropZoneArea,
	DropZoneText,
	UploadProgressCounter,
	UploadProgressBar,
	DropWrapper,
	UploadTitleText,
	StatusText,
	InitialText,
	IconWrapper,
	SuccessMessage,
	ButtonWrapper,
	ProgressText,
	FileUploadContainer,
} from './styles';

class UploadSpendCubeForm extends React.Component {
	state = {
		uploading: false,
		uploadSucceeded: false,
		uploadFailed: false,
		isInvalidFile: false,
		statusMessage: '',
		fileId: null,
		currentUploadStep: 0,
		totalUploadSteps: 1,
		errors: [],
		fileSize: '',
		fileName: '',
		isProcessing: false,
	};

	dropzoneRef = React.createRef();

	setUplaodFileId = fileId => {
		this.setState({ fileId });
	};

	onDrop = acceptedFiles => {
		const file = acceptedFiles[0];
		const allowedExtensions = /(\.xlsx)$/i;

		this.setState({
			uploadFailed: false,
			uploadSucceeded: false,
			uploading: true,
			isInvalidFile: false,
			statusMessage: '',
			errors: [],
			fileSize: bytesToSize(file.size),
			fileName: file.name,
			isProcessing: true,
		});

		if (!allowedExtensions.exec(file.path)) {
			this.setState({
				isInvalidFile: true,
				uploadFailed: true,
				uploading: false,
				statusMessage: '',
				isProcessing: false,
			});
		} else {
			uploadExcelFile(file, this.setUplaodFileId)
				.then(() => {
					this.setState({
						uploadFailed: false,
					});
				})
				.catch(errorObj => {
					const { response, ...rest } = errorObj;
					const isValidationError = response.status === 400;
					const errors = rest ? Object.values(rest) : [];

					this.setState({
						uploading: false,
						uploadFailed: true,
						statusMessage: '',
						uploadSucceeded: false,
						currentUploadStep: 0,
						totalUploadSteps: 1,
						errors: isValidationError ? errors : [],
						isProcessing: false,
					});
				});
		}
	};

	updateUploadingProgress = () => {
		const { currentUploadStep, totalUploadSteps } = this.state;
		const { clearApiCache } = this.props;
		const newState = {
			uploading: currentUploadStep < totalUploadSteps,
		};

		if (currentUploadStep === totalUploadSteps) {
			newState.currentUploadStep = 0;
			newState.totalUploadSteps = 1;
			newState.fileId = null;
			newState.uploadSucceeded = true;
			newState.isProcessing = currentUploadStep < totalUploadSteps;

			if (clearApiCache) {
				clearApiCache();
			}
		}

		this.setState(newState);
	};

	renderSuccessBlock = () => (
		<React.Fragment>
			<IconWrapper>
				<ApIcon
					iconName="outline_check_circle_outline"
					iconSize="40px"
					iconColor={brandColors.green}
				/>
			</IconWrapper>
			<DropZoneText>
				<SuccessMessage>
					{this.state.statusMessage || 'Spend cube has been updated.'}
				</SuccessMessage>
				<InitialText>Conflicts not found</InitialText>
				<ButtonWrapper>
					<ApButtonSecondary>Upload New File</ApButtonSecondary>
				</ButtonWrapper>
			</DropZoneText>
		</React.Fragment>
	);

	renderInitialBlock = () => (
		<React.Fragment>
			<IconWrapper>
				<XlsIcon width={48} height={48} />
			</IconWrapper>
			<DropZoneText>
				<InitialText>
					Drag and drop file to the area or press button to upload
				</InitialText>
				<ButtonWrapper>
					<ApButtonMain>Upload SpendCube</ApButtonMain>
				</ButtonWrapper>
			</DropZoneText>
		</React.Fragment>
	);

	renderProcessingBlock = () => {
		const { statusMessage, currentUploadStep, totalUploadSteps } = this.state;

		return (
			<React.Fragment>
				<UploadTitleText>Uploading...</UploadTitleText>
				<ProgressText>
					<StatusText>{statusMessage || 'Uploading File'}</StatusText>

					<UploadProgressCounter>
						{currentUploadStep} / {totalUploadSteps}
					</UploadProgressCounter>
				</ProgressText>
				<UploadProgressBar
					value={Math.floor((100 * currentUploadStep) / totalUploadSteps)}
				/>
			</React.Fragment>
		);
	};

	renderFailedBlock = () => (
		<React.Fragment>
			<IconWrapper>
				<ApIcon
					iconName="baseline_cancel"
					iconSize="48px"
					iconColor={grey200}
				/>
			</IconWrapper>
			<DropZoneText>
				<InitialText>
					{this.state.isInvalidFile
						? 'Invalid file type. Please be sure you are uploading a file in XLS format.'
						: 'Upload Failed'}
				</InitialText>
				<ButtonWrapper>
					<ApButtonSecondary>Upload New File</ApButtonSecondary>
				</ButtonWrapper>
			</DropZoneText>
		</React.Fragment>
	);

	renderUnsuccessBlock = () => (
		<React.Fragment>
			<IconWrapper>
				<ApIcon
					iconName="outline_error"
					iconSize="40px"
					iconColor={chartOrange}
				/>
			</IconWrapper>
			<DropZoneText>
				<SuccessMessage>Processing completed!</SuccessMessage>
				<InitialText>Conflict rows found, see details below</InitialText>
				<ButtonWrapper>
					<ApButtonSecondary>Upload New File</ApButtonSecondary>
				</ButtonWrapper>
			</DropZoneText>
		</React.Fragment>
	);

	render() {
		const {
			uploading,
			uploadFailed,
			uploadSucceeded,
			errors,
			fileSize,
			fileName,
		} = this.state;
		const isInitial = !uploading && !uploadFailed && !uploadSucceeded;

		return (
			<React.Fragment>
				<Dropzone
					ref={this.dropzoneRef}
					onDrop={this.onDrop}
					style={{}}
					disabled={uploading}
					noKeyboard
				>
					{({ getRootProps, getInputProps }) => (
						<DropWrapper {...getRootProps()}>
							<input {...getInputProps()} />
							<DropZoneArea>
								{errors.length === 0 ? (
									<React.Fragment>
										{uploading && this.renderProcessingBlock()}
										{uploadFailed && this.renderFailedBlock()}
										{!uploading && uploadSucceeded && this.renderSuccessBlock()}
										{isInitial && this.renderInitialBlock()}
									</React.Fragment>
								) : (
									this.renderUnsuccessBlock()
								)}
							</DropZoneArea>
						</DropWrapper>
					)}
				</Dropzone>

				{errors.length > 0 && (
					<FileUploadContainer>
						<ErrorList
							rowsWithErrors={errors.length}
							errors={errors}
							fileSize={fileSize}
							fileName={fileName}
						/>
					</FileUploadContainer>
				)}

				<ApSignalrConsumer
					message="StatusBarChanged"
					onMessage={(
						_0,
						statusMessage,
						fileId,
						currentUploadStep,
						totalUploadSteps
					) => {
						if (this.state.fileId === fileId) {
							if (currentUploadStep && totalUploadSteps) {
								this.setState(
									{
										statusMessage,
										currentUploadStep,
										totalUploadSteps,
									},
									this.updateUploadingProgress
								);
							} else {
								this.setState({
									statusMessage,
									uploading: false,
									uploadFailed: true,
									uploadSucceeded: false,
									isProcessing: false,
								});
							}
						}
					}}
				/>
			</React.Fragment>
		);
	}
}

export default UploadSpendCubeForm;
